import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Button,
  Form,
  Dropdown,
  Message,
  Loader,
  Header,
  Segment,
  Confirm,
  Popup
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import teamService, {
  TeamListDto,
  TeamResponseDto,
  CreateTeamDto,
  TeamContractRules,
  TeamMemberDto,
  TeamKind,
  canMutateCqvTeam,
  isCqvKind,
  normalizeTeamKind,
} from '../../services/teams/teamService';
import { userService } from '../../services/users/UserService';
import { invalidateTeamsQueries, useTeamsQuery } from '../../hooks/teams/useTeams';
import GlassmorphismTheme from '../../styles/glassmorphism';
import BaseModal, { BaseModalAction } from '../../components/shared/BaseModal';
import TeamRulesModal from '../../components/teams/TeamRulesModal';

interface User {
  id: string;
  nome: string;
  email: string;
  roles: string[];
  isActive: boolean;
}

const TeamsPage: React.FC = () => {
  const { hasRole, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const isAdmin = hasRole('Admin');
  const { data: teamsData, isLoading: loading, isError: teamsError } = useTeamsQuery({ enabled: isAdmin });
  const teams: TeamListDto[] = teamsData ?? [];
  
  const [users, setUsers] = useState<User[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<TeamResponseDto | null>(null);
  const [deletingTeam, setDeletingTeam] = useState<string | null>(null);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [selectedTeamForRules, setSelectedTeamForRules] = useState<TeamListDto | null>(null);
  const [formData, setFormData] = useState<CreateTeamDto>({
    name: '',
    managerId: '',
    salespersonIds: [],
    isTrainingTeam: false,
    kind: 'Sales',
  });
  const [vtmeFlags, setVtmeFlags] = useState<Record<string, boolean>>({});

  const canEditCqvForm = canMutateCqvTeam(user?.email, user?.id, formData.managerId);
  const formLocked = isCqvKind(formData.kind) && !canEditCqvForm;

  useEffect(() => {
    if (!isAdmin) {
      showError('Você não tem permissão para acessar esta página');
      return;
    }

    loadUsers();
  }, [isAdmin]);

  useEffect(() => {
    if (teamsError) {
      showError('Erro ao carregar times');
    }
  }, [teamsError, showError]);

  const refreshTeams = () => invalidateTeamsQueries(queryClient);

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      
      // O BaseApiService.extractData retorna response.data.data
      // Pode ser array direto ou PaginatedResponse com items
      let usersList: any[] = [];
      
      if (Array.isArray(response)) {
        // Se for array direto
        usersList = response;
      } else if (response && typeof response === 'object') {
        // Verificar se é PaginatedResponse
        if ('items' in response && Array.isArray((response as any).items)) {
          usersList = (response as any).items;
        } else if ('data' in response && Array.isArray((response as any).data)) {
          usersList = (response as any).data;
        } else {
          // Tentar usar o objeto como array (improvável mas seguro)
          usersList = [];
        }
      }
      
      const users = usersList.map((user: any) => {
        // Extrair roles corretamente
        const userRoles: string[] = [];
        if (user.userRoles && Array.isArray(user.userRoles)) {
          user.userRoles.forEach((ur: any) => {
            if (ur.role) {
              if (typeof ur.role === 'string') {
                userRoles.push(ur.role);
              } else if (ur.role.name) {
                userRoles.push(ur.role.name);
              }
            }
          });
        }
        
        return {
          id: user.id,
          nome: user.nome || user.name,
          email: user.email,
          roles: userRoles,
          isActive: user.isActive !== false,
        };
      });
      
      setUsers(users);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao carregar lista de usuários');
    }
  };

  const handleCreateTeam = () => {
    setEditingTeam(null);
    setFormData({
      name: '',
      managerId: '',
      salespersonIds: [],
      isTrainingTeam: false,
      kind: 'Sales',
    });
    setVtmeFlags({});
    setShowModal(true);
  };

  const handleEditTeam = async (teamId: string) => {
    try {
      const team = await teamService.getTeam(teamId);
      setEditingTeam(team);
      setFormData({
        name: team.name,
        managerId: team.managerId,
        salespersonIds: team.salespersons.map(sp => sp.userId),
        isTrainingTeam: team.isTrainingTeam,
        kind: normalizeTeamKind(team.kind),
      });
      const flags: Record<string, boolean> = {};
      team.salespersons.forEach(sp => {
        flags[sp.userId] = !!sp.receivesVtmeEmail;
      });
      setVtmeFlags(flags);
      setShowModal(true);
    } catch (error) {
      showError('Erro ao carregar dados do time');
    }
  };

  const handleDeleteTeam = (teamId: string) => {
    setDeletingTeam(teamId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTeam = async () => {
    if (!deletingTeam) return;

    try {
      await teamService.deleteTeam(deletingTeam);
      showSuccess('Time removido com sucesso');
      await refreshTeams();
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        showError('Só a líder do time CQV ou o administrador autorizado podem remover este time.');
      } else {
        showError('Erro ao remover time');
      }
    } finally {
      setShowDeleteConfirm(false);
      setDeletingTeam(null);
    }
  };

  const handleOpenRulesModal = (team: TeamListDto) => {
    setSelectedTeamForRules(team);
    setShowRulesModal(true);
  };

  const handleSaveRules = async (rules: TeamContractRules) => {
    if (!selectedTeamForRules) return;
    try {
      await teamService.updateContractRules(selectedTeamForRules.id, rules);
      showSuccess(`Regras do time "${selectedTeamForRules.name}" atualizadas com sucesso`);
      await refreshTeams();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar regras do time');
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      const payload: CreateTeamDto = {
        ...formData,
        isTrainingTeam: isCqvKind(formData.kind) ? false : formData.isTrainingTeam,
        memberVtmeFlags: isCqvKind(formData.kind)
          ? formData.salespersonIds.map(id => ({
              userId: id,
              receivesVtmeEmail: id !== formData.managerId && !!vtmeFlags[id],
            }))
          : [],
      };
      const errors = editingTeam
        ? teamService.validateUpdateTeamData(payload)
        : teamService.validateTeamData(payload);
      if (errors.length > 0) {
        showError(errors[0]);
        return;
      }
      if (editingTeam) {
        await teamService.updateTeam(editingTeam.id, payload);
        showSuccess('Time atualizado com sucesso');
      } else {
        await teamService.createTeam(payload);
        showSuccess('Time criado com sucesso');
      }
      
      setShowModal(false);
      await refreshTeams();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar time');
    }
  };

  const modalActions: BaseModalAction[] = [
    {
      key: 'cancel',
      content: 'Cancelar',
      icon: 'cancel',
      onClick: () => setShowModal(false),
    },
    {
      key: 'save',
      content: editingTeam ? 'Atualizar' : 'Criar',
      icon: 'save',
      primary: true,
      disabled: formLocked,
      onClick: handleSubmit,
    },
  ];

  const getTeamMembersForDisplay = (team: TeamListDto): TeamMemberDto[] => {
    if (team.salespersons && team.salespersons.length > 0) {
      return team.salespersons;
    }
    const ids = team.salespersonsIds || [];
    const names = team.salespersonsNames || [];
    return names.map((userName, index) => ({
      userId: ids[index] ?? '',
      userName,
      userEmail: '',
      isActive: true,
    }));
  };

  const getManagerOptions = () => {
    const base = users
      .filter(user => {
        const userRoles = Array.isArray(user.roles) ? user.roles : [];
        return userRoles.includes('Admin') || 
               userRoles.includes('Manager') || 
               userRoles.includes('TeamLeader');
      })
      .map(user => ({
        key: user.id,
        text: user.isActive === false
          ? `${user.nome} (${user.email}) — inativo`
          : `${user.nome} (${user.email})`,
        value: user.id
      }));
    if (
      editingTeam &&
      editingTeam.managerId &&
      !base.some(o => String(o.value) === String(editingTeam.managerId))
    ) {
      return [
        {
          key: editingTeam.managerId,
          text: `${editingTeam.managerName} (${editingTeam.managerEmail})`,
          value: editingTeam.managerId,
        },
        ...base,
      ];
    }
    return base;
  };

  const getSalespersonOptions = () => {
    const allowedRoles = isCqvKind(formData.kind) ? ['Seller', 'Admin'] : ['Seller'];
    const fromUsers = users
      .filter(userItem => {
        const userRoles = Array.isArray(userItem.roles) ? userItem.roles : [];
        return userRoles.some(role => allowedRoles.includes(role));
      })
      .map(user => ({
        key: user.id,
        text: user.isActive === false
          ? `${user.nome} (${user.email}) — inativo`
          : `${user.nome} (${user.email})`,
        value: user.id
      }));
    const seen = new Set(fromUsers.map(o => String(o.value)));
    const fromTeam =
      editingTeam?.salespersons
        ?.filter(sp => !seen.has(String(sp.userId)))
        .map(sp => {
          seen.add(String(sp.userId));
          return {
            key: sp.userId,
            text: sp.isActive === false
              ? `${sp.userName} (${sp.userEmail}) — inativo`
              : `${sp.userName} (${sp.userEmail})`,
            value: sp.userId,
          };
        }) ?? [];
    return [...fromTeam, ...fromUsers];
  };

  if (!isAdmin) {
    return (
      <GlassPageWrapper title="Times" icon="users">
        <Message error>
          <Message.Header>Acesso Negado</Message.Header>
          <p>Você não tem permissão para acessar esta página.</p>
        </Message>
      </GlassPageWrapper>
    );
  }

  if (loading) {
    return (
      <GlassPageWrapper title="Times" icon="users">
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <Loader active size="large">Carregando times...</Loader>
        </div>
      </GlassPageWrapper>
    );
  }

  return (
    <GlassPageWrapper title="Gestão de Times" icon="users">
      <div style={{ marginBottom: '12px' }}>
        <Button
          primary
          icon="plus"
          content="Novo Time"
          onClick={handleCreateTeam}
          style={GlassmorphismTheme.components.buttonPrimary}
        />
      </div>

      <Segment style={GlassmorphismTheme.components.glassCardDark}>
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold' }}>Nome</Table.HeaderCell>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold' }}>Tipo</Table.HeaderCell>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold' }}>Responsável</Table.HeaderCell>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold' }}>Membros</Table.HeaderCell>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold' }}>Criado em</Table.HeaderCell>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold' }}>Ações</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {teams.map(team => (
              <Table.Row key={team.id}>
                <Table.Cell>
                  <Header as="h4" style={{ color: '#000000' }}>
                    {team.name}
                  </Header>
                </Table.Cell>
                <Table.Cell style={{ color: '#000000' }}>
                  {isCqvKind(team.kind) ? 'CQV' : 'Venda'}
                </Table.Cell>
                <Table.Cell style={{ color: '#000000' }}>
                  {team.managerName}
                </Table.Cell>
                <Table.Cell style={{ color: '#000000' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {(() => {
                      const members = getTeamMembersForDisplay(team);
                      const count = members.length;
                      return (
                        <>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                      {count} vendedor{count !== 1 ? 'es' : ''}
                    </div>
                    {count > 0 ? (
                      <div style={{ 
                        fontSize: '0.9em', 
                        color: '#333',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px'
                      }}>
                        {members.map((m) => (
                          <span 
                            key={m.userId || m.userName}
                            style={{
                              backgroundColor: m.isActive === false ? '#e8e8e8' : '#f0f0f0',
                              color: m.isActive === false ? '#666' : '#333',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.85em',
                              border: m.isActive === false ? '1px dashed #999' : undefined,
                            }}
                          >
                            {m.userName}{m.isActive === false ? ' (inativo)' : ''}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.85em', color: '#999', fontStyle: 'italic' }}>
                        Nenhum vendedor atribuído
                      </div>
                    )}
                        </>
                      );
                    })()}
                  </div>
                </Table.Cell>
                <Table.Cell style={{ color: '#000000' }}>
                  {new Date(team.createdAt).toLocaleDateString('pt-BR')}
                </Table.Cell>
                <Table.Cell>
                  <Button.Group>
                    <Popup
                      content="Editar time"
                      trigger={
                        <Button
                          icon="edit"
                          onClick={() => handleEditTeam(team.id)}
                          style={GlassmorphismTheme.components.buttonGlass}
                        />
                      }
                    />
                    <Popup
                      content="Regras de contrato"
                      trigger={
                        <Button
                          icon="settings"
                          disabled={isCqvKind(team.kind)}
                          onClick={() => handleOpenRulesModal(team)}
                          style={GlassmorphismTheme.components.buttonGlass}
                        />
                      }
                    />
                    {(!isCqvKind(team.kind) || canMutateCqvTeam(user?.email, user?.id, team.managerId)) && (
                    <Popup
                      content="Remover time"
                      trigger={
                        <Button
                          icon="trash"
                          color="red"
                          onClick={() => handleDeleteTeam(team.id)}
                          style={GlassmorphismTheme.components.buttonGlass}
                        />
                      }
                    />
                    )}
                  </Button.Group>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>

      {/* Modal de Criação/Edição */}
      <BaseModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingTeam ? 'Editar Time' : 'Novo Time'}
        icon="users"
        size="medium"
        actions={modalActions}
      >
        <Form>
          <Form.Field>
            <label htmlFor="team-name" style={{ color: '#2c3e50' }}>Nome do Time</label>
            <Form.Input
              id="team-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Digite o nome do time"
              style={GlassmorphismTheme.components.inputGlass}
            />
          </Form.Field>
          
          <Form.Field>
            <label htmlFor="team-kind" style={{ color: '#2c3e50' }}>Tipo</label>
            <Dropdown
              id="team-kind"
              fluid
              selection
              disabled={formLocked}
              options={[
                { key: 'Sales', text: 'Venda', value: 'Sales' },
                { key: 'Cqv', text: 'CQV', value: 'Cqv' },
                { key: 'PosVenda', text: 'Pós-venda', value: 'PosVenda' },
              ]}
              value={formData.kind}
              onChange={(e, { value }) => setFormData({
                ...formData,
                kind: value as TeamKind,
                isTrainingTeam: isCqvKind(value) ? false : formData.isTrainingTeam,
              })}
              style={GlassmorphismTheme.components.inputGlass}
            />
          </Form.Field>

          <Form.Field>
            <label htmlFor="team-manager" style={{ color: '#2c3e50' }}>Responsável pelo Time</label>
            <Dropdown
              id="team-manager"
              placeholder="Selecione Admin, Manager ou TeamLeader"
              fluid
              selection
              search
              disabled={formLocked}
              options={getManagerOptions()}
              value={formData.managerId || undefined}
              onChange={(e, { value }) => setFormData({ ...formData, managerId: value as string })}
              style={{
                ...GlassmorphismTheme.components.inputGlass,
                zIndex: 9999
              }}
              noResultsMessage="Nenhum usuário encontrado"
            />
            {getManagerOptions().length === 0 && (
              <div style={{ color: '#ff6b6b', marginTop: '5px', fontSize: '0.9em' }}>
                Nenhum usuário com role Admin, Manager ou TeamLeader encontrado
              </div>
            )}
          </Form.Field>
          
          <Form.Field>
            <label htmlFor="team-salespersons" style={{ color: '#2c3e50' }}>
              {isCqvKind(formData.kind) ? 'Membros (Admin ou Seller)' : 'Vendedores (Sellers)'}
            </label>
            <Dropdown
              id="team-salespersons"
              placeholder={isCqvKind(formData.kind) ? 'Selecione os membros' : 'Selecione os vendedores'}
              fluid
              multiple
              selection
              search
              disabled={formLocked}
              options={getSalespersonOptions()}
              value={formData.salespersonIds || []}
              onChange={(e, { value }) => setFormData({ ...formData, salespersonIds: value as string[] })}
              style={{
                ...GlassmorphismTheme.components.inputGlass,
                zIndex: 9998
              }}
              noResultsMessage={isCqvKind(formData.kind) ? 'Nenhum membro encontrado' : 'Nenhum vendedor encontrado'}
            />
          </Form.Field>

          {isCqvKind(formData.kind) && (
            <Form.Field>
              <label style={{ color: '#2c3e50' }}>Recebe e-mail VTME</label>
              {formLocked && (
                <div style={{ color: '#666', fontSize: '0.9em', marginBottom: '8px' }}>
                  Só a líder do time CQV ou Davi podem alterar o pool.
                </div>
              )}
              {formData.salespersonIds
                .filter(id => id !== formData.managerId)
                .map(id => {
                  const option = getSalespersonOptions().find(o => o.value === id);
                  return (
                    <Form.Checkbox
                      key={id}
                      disabled={formLocked}
                      label={option?.text || id}
                      checked={!!vtmeFlags[id]}
                      onChange={(e, { checked }) => setVtmeFlags({ ...vtmeFlags, [id]: !!checked })}
                    />
                  );
                })}
              <div style={{ color: '#666', fontSize: '0.85em', marginTop: '6px' }}>
                A líder do time não entra no rodízio de e-mail, mesmo se estiver na lista de membros.
              </div>
            </Form.Field>
          )}
          
          {!isCqvKind(formData.kind) && (
          <Form.Field>
            <Form.Checkbox
              id="team-is-training"
              label="Turma de Treinamento"
              checked={formData.isTrainingTeam}
              onChange={(e, { checked }) => setFormData({ ...formData, isTrainingTeam: checked as boolean })}
              style={{ marginTop: '10px' }}
            />
            <div style={{ color: '#666', fontSize: '0.9em', marginTop: '5px' }}>
              Marque esta opção se este time é uma turma de treinamento. Usuários em turmas de treinamento terão acesso a questionários específicos.
            </div>
          </Form.Field>
          )}
        </Form>
      </BaseModal>

      {/* Modal de Confirmação de Exclusão */}
      <Confirm
        open={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteTeam}
        content="Tem certeza que deseja remover este time?"
        confirmButton="Remover"
        cancelButton="Cancelar"
      />

      {/* Modal de Regras de Contrato */}
      <TeamRulesModal
        open={showRulesModal}
        team={selectedTeamForRules}
        onClose={() => setShowRulesModal(false)}
        onSave={handleSaveRules}
      />
    </GlassPageWrapper>
  );
};

export default TeamsPage;
